// src/features/simulators/shared/hooks/useSimulatorWizard.ts

import { useState, useCallback, useRef, useEffect } from 'react';
import { useAnalyticsClient } from './useAnalyticsClient';
import { API_DEVICES_CONFIG } from '@/config/constants';
import type { LogEntry, CapturedRequest } from '../types/simulator.types';
import type { AnalyticsClient } from '../utils/analyticsClient';

export type SimulationStatus = 'idle' | 'preparing' | 'running' | 'completed' | 'failed';

export interface SimulationPhase {
  label: string;
  status: 'pending' | 'active' | 'completed' | 'failed';
}

export interface SimulationSummary {
  success: boolean;
  totalEvents: number;
  successful: number;
  failed: number;
  abandoned: number;
  duration: number; // ms
  error?: string;
}

interface UseSimulatorWizardProps {
  deviceType?: string;
  channel?: string;
}

interface UseSimulatorWizardReturn {
  // Wizard navigation
  activeStep: number;
  goNext: () => void;
  goBack: () => void;
  goToStep: (step: number) => void;
  canGoNext: boolean;

  // Step 1: Device selection
  hotelId: string;
  setHotelId: (id: string) => void;
  deviceId: string;
  setDeviceId: (id: string) => void;

  // Step 3: Simulation lifecycle
  simulationStatus: SimulationStatus;
  phases: SimulationPhase[];
  summary: SimulationSummary | null;
  runSimulation: (simulateFn: (client: AnalyticsClient, addLog: LogFn) => Promise<SimulationSummary>) => Promise<void>;
  resetSimulation: () => void;
  stopSimulation: () => void;

  // Logs (for EventLog)
  logs: LogEntry[];
  addLog: LogFn;

  // Captured API requests
  capturedRequests: CapturedRequest[];

  // Internal client (for simulation hooks)
  client: AnalyticsClient | null;
  isRegistered: boolean;

  // Abort signal
  abortSignal: AbortSignal | null;
}

type LogFn = (type: LogEntry['type'], message: string, details?: any) => void;

const HEARTBEAT_INTERVAL_MS = 30_000;

export const useSimulatorWizard = ({
  deviceType,
  channel,
}: UseSimulatorWizardProps = {}): UseSimulatorWizardReturn => {
  // Wizard state
  const [activeStep, setActiveStep] = useState(0);

  // Step 1 state
  const [hotelId, setHotelId] = useState('');
  const [deviceId, setDeviceId] = useState('');

  // Step 3 state
  const [simulationStatus, setSimulationStatus] = useState<SimulationStatus>('idle');
  const [phases, setPhases] = useState<SimulationPhase[]>([]);
  const [summary, setSummary] = useState<SimulationSummary | null>(null);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [capturedRequests, setCapturedRequests] = useState<CapturedRequest[]>([]);

  // Refs
  const heartbeatRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const deviceStartTimeRef = useRef<Date>(new Date());

  // Analytics client
  const { client, isRegistered, registerDevice } = useAnalyticsClient({
    apiUrl: API_DEVICES_CONFIG.BASE_URL,
  });

  // Setup request capture when client is available
  useEffect(() => {
    if (!client) return;

    const captureCallback = (req: CapturedRequest) => {
      // Skip heartbeat captures to avoid noise
      if (req.label === 'Heartbeat') return;
      setCapturedRequests(prev => [...prev, req]);
    };

    client.setRequestCapture(captureCallback);

    return () => {
      client.setRequestCapture(null);
    };
  }, [client]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Logger
  const addLog: LogFn = useCallback((type, message, details) => {
    const entry: LogEntry = {
      id: `${Date.now()}-${Math.random()}`,
      timestamp: new Date(),
      type,
      message,
      details,
    };
    setLogs(prev => [entry, ...prev].slice(0, 200));
  }, []);

  // Navigation
  const canGoNext = activeStep === 0 ? (hotelId !== '' && deviceId !== '') : true;

  const goNext = useCallback(() => {
    setActiveStep(prev => Math.min(prev + 1, 2));
  }, []);

  const goBack = useCallback(() => {
    setActiveStep(prev => Math.max(prev - 1, 0));
  }, []);

  const goToStep = useCallback((step: number) => {
    if (step >= 0 && step <= 2) {
      setActiveStep(step);
    }
  }, []);

  // Start heartbeat silently
  const startHeartbeat = useCallback(() => {
    if (!client || heartbeatRef.current) return;

    const sendHB = async () => {
      try {
        const uptimeSeconds = Math.floor((Date.now() - deviceStartTimeRef.current.getTime()) / 1000);
        await client.sendEvent({
          eventCode: 'HB',
          deviceId,
          metadata: {
            uptime: uptimeSeconds.toString(),
            status: 'idle',
            version: '1.0.0',
          },
        });
      } catch {
        // Silently ignore heartbeat failures
      }
    };

    sendHB();
    heartbeatRef.current = setInterval(sendHB, HEARTBEAT_INTERVAL_MS);
  }, [client, deviceId]);

  // Stop heartbeat
  const stopHeartbeat = useCallback(() => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
  }, []);

  // Run simulation
  const runSimulation = useCallback(async (
    simulateFn: (client: AnalyticsClient, addLog: LogFn) => Promise<SimulationSummary>
  ) => {
    if (!client) return;

    // Reset state
    setLogs([]);
    setSummary(null);
    setCapturedRequests([]);
    setSimulationStatus('preparing');
    setPhases([
      { label: 'resources.simulators.wizard.phase_connecting', status: 'active' },
      { label: 'resources.simulators.wizard.phase_running', status: 'pending' },
      { label: 'resources.simulators.wizard.phase_completing', status: 'pending' },
    ]);

    // Create abort controller
    abortControllerRef.current = new AbortController();
    deviceStartTimeRef.current = new Date();

    try {
      // Phase 1: Auto-register if needed
      if (!isRegistered) {
        addLog('info', 'Connecting device...');
        await registerDevice(deviceId);
        addLog('success', 'Device connected');
      }

      // Start heartbeat silently
      startHeartbeat();

      setPhases([
        { label: 'resources.simulators.wizard.phase_connecting', status: 'completed' },
        { label: 'resources.simulators.wizard.phase_running', status: 'active' },
        { label: 'resources.simulators.wizard.phase_completing', status: 'pending' },
      ]);

      // Phase 2: Run simulation
      setSimulationStatus('running');
      addLog('info', 'Starting test...');

      const result = await simulateFn(client, addLog);

      // Phase 3: Complete
      setPhases([
        { label: 'resources.simulators.wizard.phase_connecting', status: 'completed' },
        { label: 'resources.simulators.wizard.phase_running', status: 'completed' },
        { label: 'resources.simulators.wizard.phase_completing', status: 'completed' },
      ]);

      setSummary(result);
      setSimulationStatus('completed');
      addLog('success', 'Test completed');

    } catch (error: any) {
      const errorMessage = error?.message || 'Unknown error';
      addLog('error', `Test failed: ${errorMessage}`);

      setSummary({
        success: false,
        totalEvents: 0,
        successful: 0,
        failed: 1,
        abandoned: 0,
        duration: Date.now() - deviceStartTimeRef.current.getTime(),
        error: errorMessage,
      });

      setPhases(prev => prev.map(p =>
        p.status === 'active' ? { ...p, status: 'failed' as const } : p
      ));

      setSimulationStatus('failed');
    }
  }, [client, isRegistered, deviceId, registerDevice, startHeartbeat, addLog]);

  // Stop simulation
  const stopSimulation = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    stopHeartbeat();
    setSimulationStatus('failed');
    addLog('warning', 'Test cancelled');
  }, [stopHeartbeat, addLog]);

  // Reset for new test
  const resetSimulation = useCallback(() => {
    stopHeartbeat();
    setSimulationStatus('idle');
    setPhases([]);
    setSummary(null);
    setLogs([]);
    setCapturedRequests([]);
    setActiveStep(0);
  }, [stopHeartbeat]);

  return {
    // Navigation
    activeStep,
    goNext,
    goBack,
    goToStep,
    canGoNext,

    // Step 1
    hotelId,
    setHotelId,
    deviceId,
    setDeviceId,

    // Step 3
    simulationStatus,
    phases,
    summary,
    runSimulation,
    resetSimulation,
    stopSimulation,

    // Logs
    logs,
    addLog,

    // Captured requests
    capturedRequests,

    // Client access
    client,
    isRegistered,

    // Abort
    abortSignal: abortControllerRef.current?.signal ?? null,
  };
};
